import React, {useEffect, useState} from "react";
import {Button, Space, Tag} from "antd";
import {ReloadOutlined} from "@ant-design/icons";
import {Landmark, Users, Wallet, Coins} from "lucide-react";
import {useNavigate} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {AdminLayout} from "../../components/layout/AdminLayout";
import {GenericTable} from "../../components/table/GenericTable";
import {logout} from "../../redux/slice/accountSlice";
import {getGroupSavings, reset} from "../../redux/slice/groupSavingsSlice";
import {AppDispatch, RootState} from "../../redux/store";
import {
    HeroHeader,
    Card,
    SearchField,
    StatCard,
    StatCardGrid,
    GRADIENTS,
    COLORS,
    formatNaira,
} from "../../designSystem";

const money = (value: any) => formatNaira(value);
const moneyCell = (value: any) => <span className="font-mono">{formatNaira(value)}</span>;
const dateText = (value?: string | null) => value ? new Date(value).toLocaleString() : "No contributions";

function GroupSavings() {
    const navigate = useNavigate();
    const dispatch: AppDispatch = useDispatch();
    const {groupSavings: savings, pageInfo, error, loading, summary} = useSelector((state: RootState) => state.groupSavings);
    const {token, isAuthenticated} = useSelector((state: RootState) => state.account);
    const [searchText, setSearchText] = useState("");

    const loadGroups = (pageNumber = 1) => {
        if (!token) return;
        dispatch(getGroupSavings({token, searchParam: searchText, pageNumber})).catch((err) => console.log(err));
    };

    useEffect(() => {
        loadGroups(1);
    }, [token, searchText]);

    useEffect(() => {
        if (!isAuthenticated) {
            navigate("/login");
        }
    }, [isAuthenticated, navigate]);

    useEffect(() => {
        if (error === "Unauthorized") {
            dispatch(reset());
            dispatch(logout());
            navigate("/login");
        }
    }, [error, dispatch, navigate]);

    return (
        <AdminLayout>
            <main className="h-full overflow-y-auto px-6 py-8 md:px-8" style={{background: COLORS.surface}}>
                <HeroHeader
                    icon={Users}
                    title="Group Savings"
                    subtitle="Review group balances, contributions, categories, and members."
                    actions={
                        <button
                            onClick={() => loadGroups(pageInfo?.pageNumber || 1)}
                            disabled={loading}
                            className="inline-flex items-center gap-2 font-semibold h-12 px-6 rounded-xl border-0 cursor-pointer text-white disabled:opacity-70"
                            style={{background: "rgba(255,255,255,0.16)"}}
                        >
                            <ReloadOutlined spin={loading}/> Refresh
                        </button>
                    }
                />

                <section className="mt-8">
                    <StatCardGrid>
                        <StatCard
                            title="Groups"
                            value={(summary.totalCount || pageInfo.totalItems || 0).toLocaleString()}
                            icon={Landmark}
                            gradient={GRADIENTS.blue.tw}
                        />
                        <StatCard
                            title="Members"
                            value={(summary.totalMembers || 0).toLocaleString()}
                            icon={Users}
                            gradient={GRADIENTS.teal.tw}
                        />
                        <StatCard
                            title="Total Contributed"
                            value={money(summary.totalContributed)}
                            icon={Wallet}
                            gradient={GRADIENTS.emerald.tw}
                        />
                        <StatCard
                            title="Active Balance"
                            value={money(summary.totalActiveBalance)}
                            icon={Coins}
                            gradient={GRADIENTS.purple.tw}
                        />
                    </StatCardGrid>
                </section>

                <Card className="mt-8 p-5">
                    <SearchField
                        className="max-w-xl mb-2"
                        value={searchText}
                        onChange={(event) => setSearchText(event.target.value)}
                        placeholder="Search by group name"
                    />

                    <GenericTable
                        loading={loading}
                        data={savings}
                        pageInfo={pageInfo}
                        handlePageChange={loadGroups}
                        columns={[
                            {
                                title: "Group",
                                key: "group",
                                render: (_: any, row: any) => (
                                    <div>
                                        <button
                                            onClick={() => navigate(`/saving-groups/${row.id}`)}
                                            className="border-none bg-transparent p-0 text-left font-semibold text-blue-700 cursor-pointer"
                                        >
                                            {row.name}
                                        </button>
                                        <div className="text-xs text-slate-500">{row.description || "No description"}</div>
                                    </div>
                                ),
                            },
                            {
                                title: "Status",
                                dataIndex: "status",
                                key: "status",
                                render: (value: string) => <Tag color={value === "Active" ? "green" : "red"}>{value}</Tag>,
                            },
                            {
                                title: "Members",
                                key: "members",
                                render: (_: any, row: any) => `${row.activeMemberCount || 0}/${row.memberCount || 0}`,
                            },
                            {
                                title: "Categories",
                                dataIndex: "categoryCount",
                                key: "categoryCount",
                            },
                            {
                                title: "Contributed",
                                dataIndex: "totalContributed",
                                key: "totalContributed",
                                render: moneyCell,
                            },
                            {
                                title: "Book Balance",
                                dataIndex: "bookBalance",
                                key: "bookBalance",
                                render: moneyCell,
                            },
                            {
                                title: "Active Balance",
                                dataIndex: "activeBalance",
                                key: "activeBalance",
                                render: moneyCell,
                            },
                            {
                                title: "Last Contribution",
                                dataIndex: "lastContributionAt",
                                key: "lastContributionAt",
                                render: dateText,
                            },
                            {
                                title: "Action",
                                key: "action",
                                render: (_: any, row: any) => (
                                    <Space>
                                        <Button size="small" onClick={() => navigate(`/saving-groups/${row.id}`)}>
                                            Review
                                        </Button>
                                    </Space>
                                ),
                            },
                        ]}
                    />
                </Card>
            </main>
        </AdminLayout>
    );
}

export default GroupSavings;
